import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { AsaphButton } from '@/components/shared/Button/AsaphButton';
import { FormInput } from '@/components/shared/Inputs/FormInput';
import { TextInput } from '@/components/shared/Inputs/TextInput';
import { Logo } from '@/components/shared/Logo';
import { AsaphSwitch } from '@/components/shared/Switch';
import { UpgradeGateSheet } from '@/components/shared/UpgradeGateSheet';
import { useCreateInviteMutation } from '@/graphql';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { useTeam } from '@/providers/TeamProvider';
import {
	CircularProgress,
	FormHelperText,
	ListItem,
	ListItemText,
} from '@material-ui/core';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useCopyToClipboard } from 'react-use';
import * as Yup from 'yup';

import styles from './UserInvite.module.scss';

interface UserInviteProps {
	canAssignAdmin: boolean;
	onInviteSent?(name: string): void;
}

const validationSchema = Yup.object().shape({
	email: Yup.string()
		.email('Must be a valid email address')
		.required('Email address is required'),
});

export const UserInvite: React.FC<UserInviteProps> = ({
	canAssignAdmin,
	onInviteSent,
}) => {
	const [admin, setAdmin] = useState<boolean>(false);
	const { mutateAsync, error: inviteError } =
		useCreateInviteMutation<Error>();
	const { client } = useEnvironment();
	const { refetch, team } = useTeam();
	const adminCount = (team?.members ?? []).filter((m) => m?.roles.includes('teamAdmin')).length;
	const [showAdminsGate, setShowAdminsGate] = useState(false);
	const [value, copy] = useCopyToClipboard();
	const { enqueueSnackbar } = useSnackbar();
	const { handleError } = useErrorHandler();
	const [emailFired, setEmailFired] = useState(false);

	const fireEnterEmailEvent = useAnalyticsEvent('User_Invite_Enter_Email');
	const fireChangeMakeAdminEvent = useAnalyticsEvent(
		'User_Invite_Change_Make_Admin',
	);
	const fireInviteMemberEvent = useAnalyticsEvent(
		'User_Invite_Invite_Member',
	);
	const fireCopyLinkEvent = useAnalyticsEvent('User_Invite_Copy_Link');

	const handleToggle = (event: ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked && adminCount >= 1) {
			setShowAdminsGate(true);
			return;
		}
		setAdmin(event.target.checked);
		fireChangeMakeAdminEvent();
	};

	const inviteLink = useMemo(
		() => `${client.url}/i/${team.inviteLinkCode}`,
		[client.url, team.inviteLinkCode],
	);

	useEffect(() => {
		if (value.value) {
			enqueueSnackbar('Link copied');
		}
	}, [value, enqueueSnackbar]);

	return (
		<>
		<div className={styles['user-invite']}>
			<div className={styles['user-invite__content']}>
				<Formik
					initialValues={{
						email: '',
					}}
					validationSchema={validationSchema}
					onSubmit={async (values, helpers) => {
						try {
							await mutateAsync({
								email: values.email,
								shouldBeAdmin: admin,
							});

							fireInviteMemberEvent();
							refetch();
							enqueueSnackbar(`Invite sent to ${values.email}`, {
								variant: 'success',
							});
							if (onInviteSent) {
								onInviteSent(values.email);
							}
							helpers.resetForm({
								values: {
									email: '',
								},
							});
						} catch (error) {
							handleError({
								errorCode: 'UserInviteError',
								error,
								friendlyMessage: `There was an issue while sending the invite. Please try again.`,
							});
						}
					}}
				>
					{({
						handleSubmit,
						handleChange,
						values,
						isSubmitting,
						errors,
					}) => (
						<form onSubmit={handleSubmit}>
							<div className={styles['user-invite__logo']}>
								<Logo centered />
							</div>
							<div className="vsb-10" />
							<div
								className={styles['user-invite__content-text']}
							>
								Invite your team members with their email
								address or share the invite link
							</div>
							<div className="vsb-30" />

							<FormInput
								id="email"
								name="email"
								type="email"
								//className={styles.input}
								placeholder="Email"
								onChange={(e) => {
									handleChange(e);
									if (!emailFired) {
										fireEnterEmailEvent();
										setEmailFired(true);
									}
								}}
								value={values.email}
								disabled={isSubmitting}
								fullWidth
							/>
							{/* Alex Change to different input type ... still work ?
							<TextInput
								id="email"
								name="email"
								type="email"
								placeholder="Email"
								onChange={(e) => {
									handleChange(e);
									if (!emailFired) {
										fireEnterEmailEvent();
										setEmailFired(true);
									}
								}}
								value={values.email}
								disabled={isSubmitting}
								fullWidth
							/>*/}
							{errors.email && (
								<div className="mt-sm">
									<FormHelperText error={true}>
										{errors.email}
									</FormHelperText>
								</div>
							)}
							<div className="vsb-10" />
							{canAssignAdmin && (
								<>
									<ListItem
										className={styles['admin-switch']}
									>
										<ListItemText
											primary="Make admin"
											secondary="Can manage team members and settings"
											id="admin-switch"
											classes={{
												primary:
													styles[
														'admin-switch__text-primary'
													],
												// TODO: link with className, add module
												secondary:
													styles[
														'admin-switch__text-secondary'
													],
											}}
										/>
										<AsaphSwitch
											checked={admin}
											onChange={handleToggle}
										/>
									</ListItem>
									<div className="vsb-20" />
								</>
							)}
							<AsaphButton
								label={
									!isSubmitting ? 'Send invitation' : ''
								}
								type="submit"
								color="blue"
								disabled={isSubmitting}
								fullWidth
								endIcon={
									isSubmitting ? (
										<CircularProgress size={24.5} />
									) : null
								}
							/>
							<div className="vsb-30" />
							{/* Make sure the invite link value matches /src/config/urls.inviteLinkUrl */}

							<FormInput
								id="inviteURL"
								name="inviteURL"
								type="text"
								value={inviteLink}
								disabled={false}
								fullWidth
							/>
							{/* Making sure Alex's change doesnt break 
							<TextInput
								type="text"
								value={inviteLink}
								disabled={false}
								fullWidth
							/>
							*/}
							<div className="vsb-10" />

							<AsaphButton
								label="Copy Invite Link"
								color="grey"
								onClick={() => {
									copy(inviteLink);
									fireCopyLinkEvent();
								}}
								fullWidth
							/>
							{inviteError && (
								<div className="mt-sm">
									<FormHelperText error={true}>
										{inviteError.message}
									</FormHelperText>
								</div>
							)}
						</form>
					)}
				</Formik>
			</div>
		</div>
		<UpgradeGateSheet
			open={showAdminsGate}
			onClose={() => setShowAdminsGate(false)}
			heading="Unlimited admins"
			body="Unlimited admins are available on the Team plan. Continue to add this admin anyway."
			gateName="admins"
			onSkip={() => {
				setAdmin(true);
				fireChangeMakeAdminEvent();
			}}
			rememberAcceptance={true}
		/>
</>
	);
};
